import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type DocumentQueryView, type KycReviewDocument } from '@vello/api-client';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Field, Notice, PageHeader, Panel, ListTable, TextInput, Row, EmptyState, Drawer, DrawerHeader, DrawerBody, DrawerFooter, KeyValueList } from '@vello/web-theme';
import { DrawerStack } from '../components/drawer-stack';
import { useScopeZone } from '../components/scope-zone';
import { formatMarketInstant, loadStateReviewQueue, marketDisplayName, REGION_READ_MAX_MARKETS } from '../../src/index';
import { PersonName } from '../components/person-name';
import { MarketField } from '../components/market-field';
import { useCountryName } from '../i18n/country-name';
import { documentTypeName } from '../i18n/document-type';
import { IdRef } from '../components/code-ref';

/**
 * Aprovações (S088 manual KYC review, wired) — the driver-onboarding approval queue. The review queue is
 * per-market and scope-checked server-side (a reviewer only sees + acts on documents in their authorized
 * countries); the market is the header scope's, and the queue follows it by itself (re-read every 30 s in the
 * background), so the operator approves or rejects each document awaiting a human decision via
 * `POST /v1/kyc/documents/:id/decision`. No fabricated rows; the queue is empty until a market is in scope.
 */
const STATUS_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  REVIEW_REQUIRED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

export function ApprovalsScreen(): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const countryName = useCountryName();
  const [types, setTypes] = useState<DocumentQueryView['types'] | undefined>(undefined);
  const [market, setMarket] = useState(scope.current.city ?? '');
  const [docs, setDocs] = useState<readonly KycReviewDocument[] | undefined>(undefined);
  const [reason, setReason] = useState('');
  // The record drawer (the Directory pattern): the document stays selected while the drawer closes.
  const [openDoc, setOpenDoc] = useState<KycReviewDocument | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const { zone } = useScopeZone();
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const seq = useRef(0);
  // ADR-0124 (F2): on a STATE the queue is read market by market and merged (a reviewer's state grant covers exactly those markets); a failed market or a state beyond the bound marks the list partial.
  const region = scope.current.city === undefined ? scope.current.region : undefined;
  const [partial, setPartial] = useState<{ readonly failed: readonly string[]; readonly omitted: number } | undefined>(undefined);

  const load = useCallback(
    async (marketId: string, background = false): Promise<void> => {
      const n = ++seq.current;
      // A background read keeps the rows (and any message) on screen; a new market starts from a clean queue.
      if (!background) {
        setError(undefined);
        setMsg(undefined);
      }
      if (region !== undefined) {
        const merged = await loadStateReviewQueue(runtime.adminKyc, region, markets);
        if (n !== seq.current) return;
        if (!merged.ok) {
          setDocs([]);
          setPartial(undefined);
          setError(t('appr.error.queue', { code: fail(merged.error) }));
          return;
        }
        setError(undefined);
        setDocs(merged.value.documents);
        setPartial(merged.value.failedMarkets.length > 0 || merged.value.omittedMarkets > 0 ? { failed: merged.value.failedMarkets, omitted: merged.value.omittedMarkets } : undefined);
        const first = merged.value.readMarkets[0];
        const named = first !== undefined ? await runtime.driverRegistry.documents({ marketId: first, limit: 1 }) : undefined;
        if (n === seq.current && named?.ok === true) setTypes(named.value.types);
        return;
      }
      setPartial(undefined);
      if (marketId === '') {
        setDocs(undefined);
        return;
      }
      const res = await runtime.adminKyc.reviewQueue(marketId);
      if (n !== seq.current) return; // an older market's late answer is dropped
      if (res.ok) {
        setError(undefined);
        setDocs(res.value.documents);
        const named = await runtime.driverRegistry.documents({ marketId, limit: 1 });
        if (n === seq.current && named.ok) setTypes(named.value.types);
      } else {
        setDocs([]);
        setError(t('appr.error.queue', { code: fail(res.error) }));
      }
    },
    [runtime, t, region, markets],
  );

  // The queue follows the scoped market (or the scoped state's markets) by itself and stays current in the background (no "load" button).
  useEffect(() => {
    void load(market.trim());
  }, [market, load]);
  useAutoRefresh(() => load(market.trim(), true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market.trim() !== '' || region !== undefined });

  const openRecord = (d: KycReviewDocument, reject = false): void => {
    setOpenDoc(d);
    setDrawerOpen(true);
    setRejecting(reject);
    setReason('');
  };
  const closeDrawer = (): void => {
    setDrawerOpen(false);
    setRejecting(false);
    setReason('');
  };

  const decide = async (documentId: string, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const res = await runtime.adminKyc.decide(documentId, decision, decision === 'REJECT' ? reason : undefined);
    if (res.ok) {
      setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'appr.approved' : 'appr.rejected') });
      closeDrawer();
      await load(market.trim(), true);
    } else {
      setMsg({ tone: 'danger', text: t('appr.actionFailed', { code: fail(res.error) }) });
    }
  };

  return (
    <>
      <PageHeader title={t('appr.title')} subtitle={t('appr.subtitle')} />

      <Panel>
        {region === undefined && <MarketField id="appr-market" value={market} onChange={setMarket} hintId="appr.field.market.hint" />}
      </Panel>

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}
      {partial !== undefined && (
        <div>
          {partial.failed.length > 0 && <Notice tone="warning">{t('appr.partial.failed', { markets: partial.failed.map((m) => marketDisplayName(m, markets)).join(', ') })}</Notice>}
          {partial.omitted > 0 && <Notice tone="warning">{t('appr.partial.omitted', { max: REGION_READ_MAX_MARKETS })}</Notice>}
        </div>
      )}
      {msg !== undefined && (
        <div>
          <Notice tone={msg.tone}>{msg.text}</Notice>
        </div>
      )}

      <ListTable
        columns={[
          { key: 'doc', header: t('appr.col.doc'), render: (d: KycReviewDocument) => <IdRef value={d.id} /> },
          { key: 'owner', header: t('appr.col.owner'), render: (d: KycReviewDocument) => <PersonName id={d.ownerId} /> },
          { key: 'type', header: t('appr.col.type'), render: (d: KycReviewDocument) => documentTypeName(types, d.type, lang, v) },
          ...(scope.current.country === undefined ? [{ key: 'country', header: t('appr.col.country'), render: (d: KycReviewDocument) => countryName(d.country) }] : []),
          { key: 'number', header: t('appr.col.number'), render: (d: KycReviewDocument) => <span className="tnum">{d.numberMasked}</span> },
          { key: 'status', header: t('appr.col.status'), render: (d: KycReviewDocument) => <Badge tone={STATUS_TONE[d.status] ?? 'neutral'}>{v('kyc.document_status', d.status)}</Badge> },
        ]}
        rows={docs ?? []}
        rowKey={(d) => d.id}
        onRowClick={(d) => openRecord(d)}
        selectedKey={drawerOpen ? openDoc?.id : undefined}
        actions={(d) => (
          <Row gap={2} align="stretch" justify="end">
            <Button variant="secondary" onClick={() => void decide(d.id, 'APPROVE')}>{t('appr.approve')}</Button>
            <Button variant="danger" onClick={() => openRecord(d, true)}>{t('appr.reject')}</Button>
          </Row>
        )}
        empty={<EmptyState title={docs === undefined ? t('appr.prompt') : t('appr.empty')} />}
      />

      <Drawer open={drawerOpen} onClose={closeDrawer} labelledBy="appr-drawer-title">
        {openDoc !== undefined && (
          <>
            <DrawerHeader
              titleId="appr-drawer-title"
              title={documentTypeName(types, openDoc.type, lang, v)}
              subtitle={<PersonName id={openDoc.ownerId} />}
              actions={<Badge tone={STATUS_TONE[openDoc.status] ?? 'neutral'}>{v('kyc.document_status', openDoc.status)}</Badge>}
              onClose={closeDrawer}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                <KeyValueList
                  items={[
                    { label: t('appr.col.owner'), value: <PersonName id={openDoc.ownerId} /> },
                    { label: t('appr.col.type'), value: documentTypeName(types, openDoc.type, lang, v) },
                    { label: t('appr.col.country'), value: countryName(openDoc.country) },
                    { label: t('appr.col.number'), value: openDoc.numberMasked, mono: true },
                    ...(openDoc.expiresAt !== undefined ? [{ label: t('appr.drawer.expires'), value: formatMarketInstant(openDoc.expiresAt, zone, lang, 'date'), mono: true }] : []),
                    { label: t('appr.drawer.submitted'), value: formatMarketInstant(openDoc.createdAt, zone, lang), mono: true },
                    { label: t('appr.drawer.updated'), value: formatMarketInstant(openDoc.updatedAt, zone, lang), mono: true },
                    { label: t('appr.drawer.id'), value: <IdRef value={openDoc.id} copy full />, mono: true },
                  ]}
                />
                {rejecting && (
                  <Field label={t('appr.field.reason')} htmlFor="appr-reason">
                    <TextInput id="appr-reason" value={reason} placeholder={t('appr.field.reason.placeholder')} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {rejecting ? (
                <>
                  <Button variant="danger" onClick={() => void decide(openDoc.id, 'REJECT')}>{t('appr.reject')}</Button>
                  <Button variant="ghost" onClick={() => { setRejecting(false); setReason(''); }}>{t('ui.cancel')}</Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={() => void decide(openDoc.id, 'APPROVE')}>{t('appr.approve')}</Button>
                  <Button variant="danger" onClick={() => setRejecting(true)}>{t('appr.reject')}</Button>
                </>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
